import Link from 'next/link'
import PropTypes from 'prop-types'
import ContentfulJsonLdBlocks from '../../src/components/shared/ContentfulJsonLdBlocks'
import { formatDateMM_DD_YYYY } from 'src/common/dateFormat'
import logger from 'src/common/logger'
import Layout from 'src/components/layouts'
import AuthorsService from 'src/services/AuthorsService'
import SearchService from 'src/services/SearchService'
import TrustBundleService from 'src/services/TrustBundleService'
import Pagination from '../../src/components/shared/Pagination'
import {
  commonSchemaData,
  getTrustPilotInfo,
} from 'src/common/constants/constants'
import { getFullUrlFromReq } from 'src/common/serverUtils'
import { generateSeoData } from 'src/common/nextSeoUtils'
import { getContentfulJsonLd } from 'src/common/getContentfulJsonLd'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'

const POSTS_PER_PAGE = 26 // Limit for items per page
const PAGES = [
  {
    key: 'home',
    path: '/',
    title: 'Home',
  },
  {
    key: 'about',
    path: '/about/',
    title: 'About',
  },
  {
    key: 'contact',
    path: '/contact/',
    title: 'Contact',
  },
  {
    key: 'client-faq',
    path: '/client-faq/',
    title: 'Client FAQ',
  },
  {
    key: 'faq',
    path: '/faq/',
    title: 'FAQ',
  },
  {
    key: 'how-it-works',
    path: '/how-it-works/',
    title: 'How It Works',
  },
  {
    key: 'partner-with-the-leader-in-debt-resolution',
    path: '/partner-with-the-leader-in-debt-resolution/',
    title: 'Partner With The Leader In Debt Resolution',
  },
  {
    key: 'scam',
    path: '/scam/',
    title: 'Scam',
  },
  {
    key: 'testimonials',
    path: '/testimonials/',
    title: 'Testimonials',
  },
  {
    key: 'why-were-better',
    path: '/why-were-better/',
    title: 'Why Were Better',
  },
  {
    key: 'who-we-help',
    path: '/who-we-help/',
    title: 'Who We Help',
  },
  {
    key: 'who-we-help/states',
    path: '/who-we-help/states/',
    title: 'States',
  },
  {
    key: 'debtconsolidation',
    path: '/debtconsolidation/',
    title: 'Debt Consolidation',
  },
  {
    key: 'blog',
    path: '/blog',
    title: 'Blog',
  },
]

const buildSeoData = ({ canonicalUrl, currentPage }) =>
  generateSeoData({
    url: canonicalUrl,
    follow: true,
    index: currentPage === 1,
  })

export async function getServerSideProps({
  params: { pid: page = 1 },
  req,
  preview,
}) {
  const currentPage = Number(page[1]) || 1
  const skip = currentPage === 1 ? 0 : (currentPage - 1) * 40 - 20
  let dynamicPages = []
  let total = PAGES.length // Start total with the static pages count
  let trustMarker = {
    ratingValue: commonSchemaData?.aggregateRating?.ratingValue,
    ratingCount: commonSchemaData?.aggregateRating?.ratingCount,
    reviewCount: commonSchemaData?.aggregateRating?.reviewCount,
  }
  const pagePid = ['sitemap']
  const { canonicalUrl } = getFullUrlFromReq(req, pagePid)

  const jsonLdSchemas = await getContentfulJsonLd('sitemap/')

  try {
    const trustMarkersResponse =
      await TrustBundleService.getInstance().getCombinations(preview)
    trustMarker = getTrustPilotInfo(trustMarkersResponse, trustMarker)
  } catch (error) {
    logger.error('Sitemap - TrustBundleService error: Something went wrong', {
      error: error.message,
      stack: error.stack,
    })
  }

  try {
    // Fetch paginated dynamic pages
    const pageResponse = await SearchService.getInstance().getSitemapPages({
      limit: currentPage === 1 ? 20 : 40,
      skip,
      preview,
    })

    const authorsResponse = await AuthorsService.getInstance().getAllAuthors(
      preview
    )

    // Map dynamic pages
    dynamicPages = pageResponse.map((item) => ({
      key: item.key,
      path: '/' + item.path,
      title: item.model.title,
    }))

    // Map authors
    const authors = authorsResponse.map((author) => ({
      key: author.key,
      path: '/author/' + author.slug,
      title: author.fullName,
    }))

    // Update total count
    total += pageResponse.length + authors.length

    // Combine static pages, dynamic pages, and authors
    const allPages =
      currentPage === 1 ? [...PAGES, ...authors, ...dynamicPages] : dynamicPages

    const seoData = buildSeoData({ canonicalUrl, currentPage })

    return {
      props: {
        pages: allPages,
        date: formatDateMM_DD_YYYY(new Date()),
        currentPage,
        total,
        trustMarker,
        seoData,
        jsonLdSchemas,
      },
    }
  } catch (error) {
    logger.error('Sitemap server side: Something went wrong', {
      error: error.message,
      stack: error.stack,
    })

    const seoData = buildSeoData({ canonicalUrl, currentPage })

    return {
      props: {
        pages: PAGES, // Fallback to static pages
        date: formatDateMM_DD_YYYY(new Date()),
        currentPage,
        total,
        trustMarker,
        seoData,
        jsonLdSchemas,
      },
    }
  }
}

export default function Page({
  pages,
  date,
  currentPage,
  total,
  seoData,
  jsonLdSchemas,
}) {
  {
    console.log({ total })
  }

  return (
    <Layout
      crawlable={true}
      isBrandPage={true}
      footerClassName="mb-5 lg:mb-0"
      dashboardClass="bg-blue-300 text-white"
    >
      <WithSeoOverrides {...seoData} />
      <ContentfulJsonLdBlocks jsonLdSchemas={jsonLdSchemas} />
      <div className="mx-auto mb-12 max-w-screen-sm px-3">
        <h1 className="mb-5 pt-10 text-center text-4xl font-normal text-blue-700">
          Freedom Debt Relief Sitemap
        </h1>
        <span className="text-sm text-gray-500">
          <b>Last Updated:</b> {date}
        </span>
        <ul className="mt-4 list-disc space-y-3 pl-9 marker:text-gray-510">
          {pages.map((page) => (
            <li key={page.key} className="text-sm font-medium text-blue-560">
              <Link href={page.path} prefetch={false}>
                {page.title}
              </Link>
            </li>
          ))}
        </ul>
        <Pagination
          itemCount={total}
          currentPageNumber={currentPage}
          postsLimit={POSTS_PER_PAGE}
        />
      </div>
    </Layout>
  )
}
